import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './SmartExcelExtractor.css';

const SmartExcelExtractor = ({
  onDataExtracted,
  onCancel,
  targetLabels = [],
  parentConfig = null,
  childConfig = null,
  isDualTableMode = false,
  title = "Smart Excel Extractor",
  subtitle = "AI-powered Excel data extraction with intelligent field matching",
  initialFile = null,
  mode = 'auto' // 'auto', 'manual', 'template'
}) => {
  // Core state
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'processing', 'review', 'results'
  
  // Data state
  const [sheetData, setSheetData] = useState(null);
  const [extractedMappings, setExtractedMappings] = useState([]);
  const [confidence, setConfidence] = useState(0);
  
  // UI state
  const [dragActive, setDragActive] = useState(false);
  const [selectedMappings, setSelectedMappings] = useState(new Set());
  const [manualOverrides, setManualOverrides] = useState({});
  
  // Advanced options
  const [extractionOptions, setExtractionOptions] = useState({
    smartMode: true,
    fuzzyThreshold: 0.8,
    contextAware: true,
    multiValue: false,
    skipEmpty: true,
    confidenceThreshold: 0.6
  });

  // Excel preview states
  const [excelData, setExcelData] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [highlightedCells, setHighlightedCells] = useState(new Set());
  const [dataRange, setDataRange] = useState(null);
  
  // Resizable panel states
  const [panelSizes, setPanelSizes] = useState({ left: 50, right: 50 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartSizes, setResizeStartSizes] = useState({ left: 50, right: 50 });

  // Organize target labels for dual table mode
  const organizedTargetLabels = useMemo(() => {
    if (!isDualTableMode) {
      return { all: targetLabels };
    }
    
    const parentLabels = parentConfig?.columns?.map(col => ({
      label: col.displayName || col.label || col.name,
      type: 'parent',
      originalCol: col
    })) || [];
    
    const childLabels = childConfig?.columns?.map(col => ({
      label: col.displayName || col.label || col.name,
      type: 'child',
      originalCol: col
    })) || [];
    
    return {
      parent: parentLabels,
      child: childLabels,
      all: [...parentLabels, ...childLabels]
    };
  }, [targetLabels, parentConfig, childConfig, isDualTableMode]);

  // Auto-process initial file
  useEffect(() => {
    if (initialFile && !file) {
      setFile(initialFile);
      processFile(initialFile);
    }
  }, [initialFile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Smart Excel processing with client-side intelligence
  const processFile = useCallback(async (fileToProcess) => {
    setIsProcessing(true);
    setError(null);
    setStep('processing');
    
    try {
      // First, do client-side processing for immediate feedback
      const clientResults = await processFileClientSide(fileToProcess);
      
      if (clientResults.success) {
        setSheetData(clientResults.data);
        setExtractedMappings(clientResults.mappings);
        setConfidence(clientResults.confidence);
        setStep('review');
        
        // Pre-select high-confidence mappings
        const highConfidenceMappings = new Set(
          clientResults.mappings
            .filter(m => m.confidence >= 0.8)
            .map(m => m.id)
        );
        setSelectedMappings(highConfidenceMappings);
      } else {
        throw new Error(clientResults.error || 'Failed to process Excel file');
      }
      
    } catch (err) {
      console.error('File processing error:', err);
      setError(err.message || 'Failed to process file');
      setStep('upload');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Client-side Excel processing using XLSX library
  const processFileClientSide = async (fileToProcess) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          // Import XLSX dynamically to avoid loading issues
          import('xlsx').then(XLSX => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // Process the first sheet (or most relevant sheet)
            const sheetName = selectBestSheet(workbook.SheetNames);
            const worksheet = workbook.Sheets[sheetName];
            
            // Store Excel data for preview
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1, 
              defval: '', 
              raw: false 
            });
            setExcelData({
              workbook,
              worksheet,
              jsonData,
              sheetNames: workbook.SheetNames,
              currentSheet: sheetName
            });

            // Calculate data range
            const range = calculateDataRange(worksheet);
            setDataRange(range);
            
            // Convert to array format for processing
            const rawData = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1, 
              defval: '',
              range: 0 // Start from first row
            });
            
            // Smart extraction using multiple algorithms
            const extractionResult = performSmartExtraction(rawData, organizedTargetLabels.all.map(item => item.label || item), extractionOptions);
            
            resolve({
              success: true,
              data: {
                sheetName,
                rawData: rawData.slice(0, 20), // Preview only
                totalRows: rawData.length,
                totalCols: rawData[0]?.length || 0
              },
              mappings: extractionResult.mappings,
              confidence: extractionResult.confidence,
              metadata: extractionResult.metadata
            });
          }).catch(err => {
            resolve({
              success: false,
              error: `XLSX library error: ${err.message}`
            });
          });
        } catch (err) {
          resolve({
            success: false,
            error: `File parsing error: ${err.message}`
          });
        }
      };
      
      reader.onerror = () => {
        resolve({
          success: false,
          error: 'Failed to read file'
        });
      };
      
      reader.readAsArrayBuffer(fileToProcess);
    });
  };

  // Select the best sheet for data extraction
  const selectBestSheet = (sheetNames) => {
    // Prioritize sheets with data-like names
    const dataPatterns = ['data', 'main', 'sheet1', 'input', 'form', 'report'];
    const avoidPatterns = ['summary', 'template', 'blank', 'instructions'];
    
    // First, avoid sheets with bad patterns
    const goodSheets = sheetNames.filter(name => 
      !avoidPatterns.some(pattern => 
        name.toLowerCase().includes(pattern)
      )
    );
    
    // Then prioritize sheets with good patterns
    const bestSheet = goodSheets.find(name => 
      dataPatterns.some(pattern => 
        name.toLowerCase().includes(pattern)
      )
    );
    
    return bestSheet || goodSheets[0] || sheetNames[0];
  };

  // Advanced smart extraction with multiple algorithms
  const performSmartExtraction = (rawData, labels, options) => {
    const mappings = [];
    const metadata = {
      totalCells: 0,
      processedCells: 0,
      algorithms: [],
      patterns: []
    };
    
    if (!rawData || rawData.length === 0) {
      return { mappings: [], confidence: 0, metadata };
    }

    // Algorithm 1: Direct Label-Value Pairs (horizontal)
    const horizontalMappings = extractHorizontalPairs(rawData, labels, options);
    mappings.push(...horizontalMappings);
    if (horizontalMappings.length > 0) metadata.algorithms.push('horizontal-pairs');

    // Algorithm 2: Vertical Label-Value Pairs
    const verticalMappings = extractVerticalPairs(rawData, labels, options);
    mappings.push(...verticalMappings);
    if (verticalMappings.length > 0) metadata.algorithms.push('vertical-pairs');

    // Algorithm 3: Form-style extraction (label above value)
    const formMappings = extractFormStyle(rawData, labels, options);
    mappings.push(...formMappings);
    if (formMappings.length > 0) metadata.algorithms.push('form-style');

    // Algorithm 4: Table header extraction
    const tableMappings = extractTableHeaders(rawData, labels, options);
    mappings.push(...tableMappings);
    if (tableMappings.length > 0) metadata.algorithms.push('table-headers');

    // Deduplicate and rank mappings
    const uniqueMappings = deduplicateAndRank(mappings, options);
    
    // Calculate overall confidence
    const confidence = calculateOverallConfidence(uniqueMappings);
    
    return {
      mappings: uniqueMappings,
      confidence,
      metadata: {
        ...metadata,
        totalMappings: uniqueMappings.length,
        targetLabels: labels.length,
        successRate: labels.length > 0 ? uniqueMappings.length / labels.length : 0
      }
    };
  };

  // Extract horizontal label-value pairs (Label: Value)
  const extractHorizontalPairs = (data, labels, options) => {
    const mappings = [];
    
    for (let row = 0; row < data.length; row++) {
      const rowData = data[row] || [];
      
      for (let col = 0; col < rowData.length - 1; col++) {
        const cellValue = getCellValue(rowData[col]);
        if (!cellValue) continue;
        
        const matchResult = findBestLabelMatch(cellValue, labels, options);
        if (matchResult.confidence >= options.fuzzyThreshold) {
          const nextValue = getCellValue(rowData[col + 1]);
          
          if (nextValue && isValidDataValue(nextValue)) {
            mappings.push({
              id: `h_${row}_${col}`,
              label: matchResult.targetLabel,
              detectedLabel: cellValue,
              value: nextValue,
              confidence: matchResult.confidence,
              method: 'horizontal-pair',
              position: { row, col },
              valuePosition: { row, col: col + 1 }
            });
          }
        }
      }
    }
    
    return mappings;
  };

  // Extract vertical label-value pairs (Label on top, value below)
  const extractVerticalPairs = (data, labels, options) => {
    const mappings = [];
    
    for (let row = 0; row < data.length - 1; row++) {
      const rowData = data[row] || [];
      
      for (let col = 0; col < rowData.length; col++) {
        const cellValue = getCellValue(rowData[col]);
        if (!cellValue) continue;
        
        const matchResult = findBestLabelMatch(cellValue, labels, options);
        if (matchResult.confidence >= options.fuzzyThreshold) {
          const belowValue = getCellValue(data[row + 1]?.[col]);
          
          if (belowValue && isValidDataValue(belowValue)) {
            mappings.push({
              id: `v_${row}_${col}`,
              label: matchResult.targetLabel,
              detectedLabel: cellValue,
              value: belowValue,
              confidence: matchResult.confidence,
              method: 'vertical-pair',
              position: { row, col },
              valuePosition: { row: row + 1, col }
            });
          }
        }
      }
    }
    
    return mappings;
  };

  // Extract form-style mappings (more complex patterns)
  const extractFormStyle = (data, labels, options) => {
    const mappings = [];
    
    // Look for patterns like:
    // Label:
    // Value
    // or
    // Label: Value (same cell)
    
    for (let row = 0; row < data.length; row++) {
      const rowData = data[row] || [];
      
      for (let col = 0; col < rowData.length; col++) {
        const cellValue = getCellValue(rowData[col]);
        if (!cellValue) continue;
        
        // Check if cell contains both label and value (Label: Value)
        const colonIndex = cellValue.indexOf(':');
        if (colonIndex > 0 && colonIndex < cellValue.length - 1) {
          const labelPart = cellValue.substring(0, colonIndex).trim();
          const valuePart = cellValue.substring(colonIndex + 1).trim();
          
          const matchResult = findBestLabelMatch(labelPart, labels, options);
          if (matchResult.confidence >= options.fuzzyThreshold && isValidDataValue(valuePart)) {
            mappings.push({
              id: `f_${row}_${col}`,
              label: matchResult.targetLabel,
              detectedLabel: labelPart,
              value: valuePart,
              confidence: matchResult.confidence * 0.95, // Slight penalty for same-cell
              method: 'form-same-cell',
              position: { row, col },
              valuePosition: { row, col }
            });
          }
        }
      }
    }
    
    return mappings;
  };

  // Extract table header mappings
  const extractTableHeaders = (data, labels, options) => {
    const mappings = [];
    
    // Find rows that look like headers (multiple matches)
    for (let row = 0; row < Math.min(5, data.length); row++) {
      const rowData = data[row] || [];
      let matches = 0;
      const rowMappings = [];
      
      for (let col = 0; col < rowData.length; col++) {
        const cellValue = getCellValue(rowData[col]);
        if (!cellValue) continue;
        
        const matchResult = findBestLabelMatch(cellValue, labels, options);
        if (matchResult.confidence >= options.fuzzyThreshold) {
          matches++;
          
          // Get first non-empty value from this column
          let dataValue = null;
          for (let dataRow = row + 1; dataRow < Math.min(row + 10, data.length); dataRow++) {
            const value = getCellValue(data[dataRow]?.[col]);
            if (value && isValidDataValue(value)) {
              dataValue = value;
              break;
            }
          }
          
          if (dataValue) {
            rowMappings.push({
              id: `t_${row}_${col}`,
              label: matchResult.targetLabel,
              detectedLabel: cellValue,
              value: dataValue,
              confidence: matchResult.confidence * 0.9, // Table headers get slight penalty
              method: 'table-header',
              position: { row, col },
              valuePosition: { row: row + 1, col } // Approximate
            });
          }
        }
      }
      
      // If this row has multiple matches, it's likely a header row
      if (matches >= 2) {
        mappings.push(...rowMappings);
        break; // Use first header row found
      }
    }
    
    return mappings;
  };

  // Smart label matching with fuzzy logic
  const findBestLabelMatch = (cellValue, labels, options) => {
    let bestMatch = { targetLabel: null, confidence: 0 };
    
    const normalizedCell = normalizeLabel(cellValue);
    
    for (const label of labels) {
      const normalizedLabel = normalizeLabel(label);
      
      // Exact match
      if (normalizedCell === normalizedLabel) {
        return { targetLabel: label, confidence: 1.0 };
      }
      
      // Fuzzy match
      const similarity = calculateSimilarity(normalizedCell, normalizedLabel);
      if (similarity > bestMatch.confidence) {
        bestMatch = { targetLabel: label, confidence: similarity };
      }
      
      // Partial match (contains)
      if (normalizedCell.includes(normalizedLabel) || normalizedLabel.includes(normalizedCell)) {
        const containsSimilarity = Math.min(normalizedCell.length, normalizedLabel.length) / 
                                 Math.max(normalizedCell.length, normalizedLabel.length);
        if (containsSimilarity > bestMatch.confidence) {
          bestMatch = { targetLabel: label, confidence: containsSimilarity * 0.9 };
        }
      }
    }
    
    return bestMatch;
  };

  // Normalize labels for better matching
  const normalizeLabel = (label) => {
    return String(label)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
      .replace(/\s+/g, ' ') // Normalize spaces
      .replace(/\b(the|a|an)\b/g, '') // Remove articles
      .trim();
  };

  // Calculate string similarity (Jaro-Winkler-like)
  const calculateSimilarity = (str1, str2) => {
    if (str1 === str2) return 1.0;
    
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = levenshteinDistance(str1, str2);
    return (longer.length - editDistance) / longer.length;
  };

  // Levenshtein distance calculation
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  // Deduplicate and rank mappings
  const deduplicateAndRank = (mappings, options) => {
    const labelMap = new Map();
    
    // Group by target label
    mappings.forEach(mapping => {
      const key = mapping.label;
      if (!labelMap.has(key)) {
        labelMap.set(key, []);
      }
      labelMap.get(key).push(mapping);
    });
    
    // Select best mapping for each label
    const uniqueMappings = [];
    labelMap.forEach((candidates, label) => {
      // Sort by confidence, then by method preference
      candidates.sort((a, b) => {
        if (Math.abs(a.confidence - b.confidence) > 0.1) {
          return b.confidence - a.confidence;
        }
        
        // Method preference: horizontal > vertical > form > table
        const methodPriority = {
          'horizontal-pair': 4,
          'vertical-pair': 3,
          'form-same-cell': 2,
          'table-header': 1
        };
        
        return (methodPriority[b.method] || 0) - (methodPriority[a.method] || 0);
      });
      
      uniqueMappings.push(candidates[0]);
    });
    
    return uniqueMappings;
  };

  // Calculate overall confidence
  const calculateOverallConfidence = (mappings) => {
    if (mappings.length === 0) return 0;
    
    const totalConfidence = mappings.reduce((sum, m) => sum + m.confidence, 0);
    return totalConfidence / mappings.length;
  };

  // Utility functions
  const getCellValue = (cell) => {
    if (cell === null || cell === undefined) return null;
    const str = String(cell).trim();
    return str.length > 0 ? str : null;
  };

  const isValidDataValue = (value) => {
    if (!value) return false;
    const str = String(value).trim();
    
    // Skip obvious non-data values
    const skipPatterns = [
      /^[^\w\d]*$/, // Only punctuation
      /^(n\/a|na|null|none|empty|blank|tbd|todo)$/i,
      /^(label|field|column|header)$/i
    ];
    
    return !skipPatterns.some(pattern => pattern.test(str));
  };

  // File handling
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    // Validate file type
    if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    processFile(selectedFile);
  };

  // Mapping management
  const toggleMapping = (mappingId) => {
    const newSelected = new Set(selectedMappings);
    if (newSelected.has(mappingId)) {
      newSelected.delete(mappingId);
    } else {
      newSelected.add(mappingId);
    }
    setSelectedMappings(newSelected);
  };

  const handleManualOverride = (mappingId, newValue) => {
    setManualOverrides(prev => ({
      ...prev,
      [mappingId]: newValue
    }));
  };

  const handleApply = () => {
    const selectedMappingData = extractedMappings
      .filter(m => selectedMappings.has(m.id))
      .reduce((acc, mapping) => {
        const finalValue = manualOverrides[mapping.id] || mapping.value;
        acc[mapping.label] = finalValue;
        return acc;
      }, {});

    const results = {
      success: true,
      results: {
        mappings: selectedMappingData,
        metadata: {
          totalLabelsSearched: targetLabels.length,
          labelsFound: selectedMappings.size,
          extractionMethod: {},
          confidence: {},
          cellPositions: {}
        }
      },
      summary: {
        totalLabels: targetLabels.length,
        foundLabels: selectedMappings.size,
        successRate: `${Math.round((selectedMappings.size / targetLabels.length) * 100)}%`,
        extractedData: selectedMappings.size
      }
    };

    // Add metadata for each selected mapping
    extractedMappings
      .filter(m => selectedMappings.has(m.id))
      .forEach(mapping => {
        results.results.metadata.extractionMethod[mapping.label] = mapping.method;
        results.results.metadata.confidence[mapping.label] = mapping.confidence;
        results.results.metadata.cellPositions[mapping.label] = {
          label: mapping.position,
          value: mapping.valuePosition
        };
      });

    onDataExtracted(results);
  };

  // Handle panel resizing
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStartX(e.clientX);
    setResizeStartSizes(panelSizes);
    
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const container = e.target.closest('.review-content');
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const deltaX = e.clientX - resizeStartX;
      const deltaPercent = (deltaX / containerRect.width) * 100;
      
      const newLeftSize = Math.max(30, Math.min(70, resizeStartSizes.left + deltaPercent));
      const newRightSize = 100 - newLeftSize;
      
      setPanelSizes({ left: newLeftSize, right: newRightSize });
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isResizing, resizeStartX, resizeStartSizes, panelSizes]);

  // Calculate data range from Excel
  const calculateDataRange = useCallback((worksheet) => {
    if (!worksheet) return null;
    
    const range = worksheet['!ref'];
    if (!range) return null;
    
    // Parse range like "A1:D10"
    const [start, end] = range.split(':');
    const startCol = start.match(/[A-Z]+/)[0];
    const startRow = parseInt(start.match(/\d+/)[0]);
    const endCol = end.match(/[A-Z]+/)[0];
    const endRow = parseInt(end.match(/\d+/)[0]);
    
    return {
      startCol,
      startRow,
      endCol,
      endRow,
      range
    };
  }, []);

  // Render organized mappings list for dual table mode
  const renderMappingsList = () => {
    if (!isDualTableMode) {
      return renderSingleTableMappings();
    }

    const parentMappings = extractedMappings.filter(m => 
      organizedTargetLabels.parent.some(p => p.label === m.label)
    );
    const childMappings = extractedMappings.filter(m => 
      organizedTargetLabels.child.some(c => c.label === m.label)
    );

    return (
      <div className="dual-table-mappings">
        {/* Parent Table Fields */}
        {parentMappings.length > 0 && (
          <div className="mapping-section">
            <div className="section-header">
              <h6>📋 Parent Table Fields ({parentMappings.length})</h6>
            </div>
            {renderMappingItems(parentMappings)}
          </div>
        )}

        {/* Child Table Fields */}
        {childMappings.length > 0 && (
          <div className="mapping-section">
            <div className="section-header">
              <h6>📄 Child Table Fields ({childMappings.length})</h6>
            </div>
            {renderMappingItems(childMappings)}
          </div>
        )}

        {/* No Mappings Found */}
        {extractedMappings.length === 0 && (
          <div className="no-mappings-compact">
            <div className="no-data-icon">🔍</div>
            <h4>No Mappings Found</h4>
            <p>The smart extractor couldn't find any matching fields in your Excel file.</p>
          </div>
        )}
      </div>
    );
  };

  const renderSingleTableMappings = () => {
    return (
      <div className="single-table-mappings">
        {extractedMappings.length > 0 ? renderMappingItems(extractedMappings) : (
          <div className="no-mappings-compact">
            <div className="no-data-icon">🔍</div>
            <h4>No Mappings Found</h4>
            <p>The smart extractor couldn't find any matching fields in your Excel file.</p>
          </div>
        )}
      </div>
    );
  };

  const renderMappingItems = (mappings) => {
    return mappings.map((mapping) => (
      <div 
        key={mapping.id} 
        className={`mapping-item-compact ${selectedMappings.has(mapping.id) ? 'selected' : ''}`}
      >
        <div className="mapping-row">
          <div className="mapping-checkbox">
            <input
              type="checkbox"
              checked={selectedMappings.has(mapping.id)}
              onChange={() => toggleMapping(mapping.id)}
            />
          </div>
          
          <div className="mapping-field">
            <span className="field-label" title={mapping.label}>
              {mapping.label}
            </span>
          </div>
          
          <div className="mapping-arrow">→</div>
          
          <div className="mapping-value">
            <input
              type="text"
              value={manualOverrides[mapping.id] || mapping.value}
              onChange={(e) => handleManualOverride(mapping.id, e.target.value)}
              className="value-input-compact"
              placeholder="Value"
            />
          </div>
          
          <div className="mapping-meta">
            <span className="confidence-badge-compact">
              {Math.round(mapping.confidence * 100)}%
            </span>
            <span className="method-badge-compact">
              {mapping.method.split('-')[0]}
            </span>
            <span className="position-badge-compact">
              {String.fromCharCode(65 + mapping.position.col)}{mapping.position.row + 1}
            </span>
          </div>
        </div>
      </div>
    ));
  };

  // Render Excel preview with data range highlighting
  const renderExcelPreview = () => {
    if (!excelData || !excelData.jsonData) {
      return <div className="preview-placeholder">No Excel data available</div>;
    }

    const data = excelData.jsonData.slice(0, 20); // Show first 20 rows
    const maxCols = Math.max(...data.map(row => row.length), 10);

    return (
      <table className="excel-preview-table">
        <thead>
          <tr>
            <th className="row-header">#</th>
            {Array.from({ length: maxCols }, (_, i) => (
              <th key={i} className="col-header">{String.fromCharCode(65 + i)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <td className="row-header">{rowIndex + 1}</td>
              {Array.from({ length: maxCols }, (_, colIndex) => {
                const cellValue = row[colIndex] || '';
                
                // Find if this cell is a label or value in our mappings
                const isLabel = extractedMappings.find(m => 
                  m.position.row === rowIndex && m.position.col === colIndex
                );
                const isValue = extractedMappings.find(m => 
                  m.valuePosition && m.valuePosition.row === rowIndex && m.valuePosition.col === colIndex
                );
                
                // Check if this mapping is selected
                const isSelected = (isLabel && selectedMappings.has(isLabel.id)) || 
                                 (isValue && selectedMappings.has(isValue.id));
                
                // Check if cell is in data range
                const isInDataRange = dataRange && 
                  rowIndex >= dataRange.startRow - 1 && 
                  rowIndex <= dataRange.endRow - 1 &&
                  colIndex >= dataRange.startCol.charCodeAt(0) - 65 &&
                  colIndex <= dataRange.endCol.charCodeAt(0) - 65;
                
                let cellClass = 'excel-cell';
                let cellTitle = '';
                
                if (isInDataRange) {
                  cellClass += ' data-range-highlight';
                }
                
                if (isLabel) {
                  cellClass += isSelected ? ' label-selected' : ' label-highlight';
                  cellTitle = `Label: ${isLabel.label} (${Math.round(isLabel.confidence * 100)}%)`;
                } else if (isValue) {
                  cellClass += isSelected ? ' value-selected' : ' value-highlight';
                  cellTitle = `Value for: ${isValue.label}`;
                }
                
                return (
                  <td 
                    key={colIndex} 
                    className={cellClass}
                    title={cellTitle}
                    onClick={() => {
                      // Highlight the corresponding mapping when cell is clicked
                      if (isLabel || isValue) {
                        const mapping = isLabel || isValue;
                        if (mapping) {
                          toggleMapping(mapping.id);
                        }
                      }
                    }}
                  >
                    <div className="cell-content">
                      {cellValue || ''}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Render components
  const renderUploadStep = () => (
    <div className="smart-extractor-step upload-step">
      <div className="step-header">
        <h3>📤 Upload Excel File</h3>
        <p>Drop your Excel file here for intelligent data extraction</p>
      </div>

      <div 
        className={`file-drop-zone ${dragActive ? 'active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="drop-zone-content">
          <div className="upload-icon">🧠</div>
          <h4>Smart Excel Extraction</h4>
          <p>AI-powered field detection and mapping</p>
          
          <label className="file-input-label">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <span className="btn btn-primary">Choose Excel File</span>
          </label>
          
          <div className="features-list">
            <div className="feature">✨ Intelligent field matching</div>
            <div className="feature">🎯 Multiple extraction algorithms</div>
            <div className="feature">⚡ Real-time processing</div>
            <div className="feature">🔧 Manual override options</div>
          </div>
        </div>
      </div>

      {targetLabels.length > 0 && (
        <div className="target-labels-preview">
          <h5>🎯 Target Fields ({targetLabels.length})</h5>
          <div className="labels-grid">
            {targetLabels.slice(0, 12).map((label, index) => (
              <span key={index} className="label-chip">{label}</span>
            ))}
            {targetLabels.length > 12 && (
              <span className="more-labels">+{targetLabels.length - 12} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderProcessingStep = () => (
    <div className="smart-extractor-step processing-step">
      <div className="processing-animation">
        <div className="processing-spinner"></div>
        <h3>🧠 Analyzing Excel File</h3>
        <p>Running intelligent extraction algorithms...</p>
        
        <div className="processing-steps">
          <div className="process-step active">📊 Reading Excel data</div>
          <div className="process-step active">🔍 Detecting patterns</div>
          <div className="process-step active">🎯 Matching fields</div>
          <div className="process-step">✨ Optimizing results</div>
        </div>
      </div>
      
      {file && (
        <div className="file-info">
          <span className="file-name">{file.name}</span>
          <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
        </div>
      )}
    </div>
  );

  const renderReviewStep = () => (
    <div className="smart-extractor-step review-step">
      <div className="step-header">
        <h3>📋 Review Extracted Data</h3>
        <p>Review and select the mappings you want to apply</p>
        
        <div className="extraction-stats">
          <div className="stat">
            <span className="stat-value">{extractedMappings.length}</span>
            <span className="stat-label">Fields Found</span>
          </div>
          <div className="stat">
            <span className="stat-value">{Math.round(confidence * 100)}%</span>
            <span className="stat-label">Confidence</span>
          </div>
          <div className="stat">
            <span className="stat-value">{selectedMappings.size}</span>
            <span className="stat-label">Selected</span>
          </div>
        </div>
      </div>

      <div className="review-content">
        {/* Left Panel - Mappings */}
        <div 
          className="mappings-panel" 
          style={{ width: `${panelSizes.left}%` }}
        >
          <div className="mappings-header">
            <h5>📋 Field Mappings ({extractedMappings.length})</h5>
            <div className="mappings-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedMappings(new Set(extractedMappings.map(m => m.id)))}
              >
                Select All
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedMappings(new Set())}
              >
                Clear All
              </button>
            </div>
          </div>

          <div className="mappings-list-compact">
            {renderMappingsList()}
          </div>
        </div>

        {/* Resize Handle */}
        <div 
          className="resize-handle"
          onMouseDown={handleResizeStart}
        >
          <div className="resize-line"></div>
        </div>

        {/* Right Panel - Excel Preview */}
        <div 
          className="excel-preview-panel"
          style={{ width: `${panelSizes.right}%` }}
        >
          <div className="preview-header">
            <h5>📊 Excel Preview</h5>
            <div className="preview-legend">
              <div className="legend-item">
                <div className="legend-color label-highlight"></div>
                <span>Labels</span>
              </div>
              <div className="legend-item">
                <div className="legend-color value-highlight"></div>
                <span>Values</span>
              </div>
              <div className="legend-item">
                <div className="legend-color selected-highlight"></div>
                <span>Selected</span>
              </div>
              {dataRange && (
                <div className="legend-item">
                  <div className="legend-color data-range-highlight"></div>
                  <span>Data Range</span>
                </div>
              )}
            </div>
          </div>

          <div className="excel-preview-container">
            {renderExcelPreview()}
          </div>
        </div>
      </div>

      <div className="extraction-options-compact">
        <h5>⚙️ Options</h5>
        <div className="options-row">
          <label className="option-item-compact">
            <input
              type="checkbox"
              checked={extractionOptions.smartMode}
              onChange={(e) => setExtractionOptions(prev => ({
                ...prev,
                smartMode: e.target.checked
              }))}
            />
            <span>Smart Mode</span>
          </label>
          
          <label className="option-item-compact">
            <input
              type="checkbox"
              checked={extractionOptions.contextAware}
              onChange={(e) => setExtractionOptions(prev => ({
                ...prev,
                contextAware: e.target.checked
              }))}
            />
            <span>Context Aware</span>
          </label>
          
          <label className="option-item-compact">
            <input
              type="checkbox"
              checked={extractionOptions.skipEmpty}
              onChange={(e) => setExtractionOptions(prev => ({
                ...prev,
                skipEmpty: e.target.checked
              }))}
            />
            <span>Skip Empty</span>
          </label>
          
          <div className="option-item-compact">
            <label>Threshold: {Math.round(extractionOptions.fuzzyThreshold * 100)}%</label>
            <input
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={extractionOptions.fuzzyThreshold}
              onChange={(e) => setExtractionOptions(prev => ({
                ...prev,
                fuzzyThreshold: parseFloat(e.target.value)
              }))}
              className="threshold-slider"
            />
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={() => setStep('upload')}>
          ← Upload Different File
        </button>
        <button className="btn btn-secondary" onClick={() => processFile(file)}>
          🔄 Re-analyze
        </button>
        <button 
          className="btn btn-primary"
          onClick={handleApply}
          disabled={selectedMappings.size === 0}
        >
          Apply Selected ({selectedMappings.size})
        </button>
      </div>
    </div>
  );

  // Main render
  return (
    <div className="smart-excel-extractor">
      <div className="extractor-modal">
        <div className="extractor-header">
          <div className="header-content">
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        <div className="extractor-content">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {step === 'upload' && renderUploadStep()}
          {step === 'processing' && renderProcessingStep()}
          {step === 'review' && renderReviewStep()}
        </div>
      </div>
    </div>
  );
};

export default SmartExcelExtractor; 